import {
  ThunkAction,
  UnknownAction,
  combineReducers,
  configureStore,
} from '@reduxjs/toolkit';
import undoable from 'redux-undo';

import copyPasteReducer from './features/copyPaste/copyPasteSlice';
import graphReducer, {
  deselectNodesEdges,
  selectAllNodesAndEdges,
  updateNodesEdgesSelection,
} from './features/graph/graphSlice';
import uiReducer from './features/ui/uiSlice';
import workflowReducer from './features/workflow/workflowSlice';
import { loadExistingWorkflow } from './migration';

// ===== UNDOABLE ===============================================================

// actions excluded from the redux-undo history
const excludedUndoActions: readonly string[] = [
  deselectNodesEdges.type,
  updateNodesEdgesSelection.type,
  selectAllNodesAndEdges.type,
  loadExistingWorkflow.type,
];

// "thePrefix/actionName" actions will get filtered from redux-undo history, eg "ui/foo"
const excludedUndoActionPrefixes: readonly string[] = ['ui'];

const undoRedoReducers = combineReducers({
  graph: graphReducer,
  workflow: workflowReducer,
});

const undoableState = undoable(undoRedoReducers, {
  ignoreInitialState: true, // ensure that loading existing workflow is not undoable
  syncFilter: true,
  filter: (action, _currentState, _previousHistory) => {
    // filter all excluded actions
    if (excludedUndoActions.includes(action.type)) {
      return false;
    }

    // filter all excluded action prefixes
    if (
      excludedUndoActionPrefixes.some((prefix) =>
        action.type.startsWith(`${prefix}/`)
      )
    ) {
      return false;
    }

    return true;
  },
});

// ==============================================================================

export const rootReducer = combineReducers({
  undoableState,
  copyPaste: copyPasteReducer,
  ui: uiReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

export const createStore = () =>
  configureStore({
    reducer: rootReducer,
    devTools: {
      // Sanitize 'past' of undoableState so Redux DevTools doesn't struggle serializing it
      stateSanitizer: (state: any) =>
        ({
          ...state,
          undoableState: {
            ...state.undoableState,
            past: '<<PAST>> (sanitized for DevTools)',
          },
        } as any),
    },
  });

export type AppStore = ReturnType<typeof createStore>;

export type AppDispatch = AppStore['dispatch'];

// For createAsyncThunk
export interface AppThunkAPI {
  state: RootState;
  dispatch: AppDispatch;
}
// For synchronous thunks
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  UnknownAction
>;

export type UndoableState = ReturnType<typeof undoableState>;
